/** Klient na komunikáciu so serverom. */

const STORAGE_KEY = 'fs_token';
const SERVER_KEY = 'fs_server';

export function getServerUrl() {
  // v Electrone / pri inej adrese servera sa dá nastaviť
  const saved = localStorage.getItem(SERVER_KEY);
  if (saved) return saved.replace(/\/$/, '');
  if (window.FS_SERVER_URL) return String(window.FS_SERVER_URL).replace(/\/$/, '');
  return '';
}
export function setServerUrl(url) {
  if (url) localStorage.setItem(SERVER_KEY, url.replace(/\/$/, ''));
  else localStorage.removeItem(SERVER_KEY);
}

export const getToken = () => localStorage.getItem(STORAGE_KEY);

/**
 * Popri prihlásení si odložíme aj neutrálnu značku do cookie.
 *
 * Katalóg sa vykresľuje na serveri a ten o localStorage nevie. Vďaka tejto
 * značke vie server dopredu rozhodnúť, či má stránke pridať zamestnanecké
 * ovládanie – zákazníkovi sa tak do stránky nedostane ani skryté. Cookie
 * neobsahuje nič citlivé (len „1"), prihlásenie sa aj tak overuje tokenom.
 */
function markStaffCookie(on) {
  try {
    document.cookie = on
      ? 'fs_staff=1; path=/; max-age=2592000; SameSite=Lax'
      : 'fs_staff=; path=/; max-age=0; SameSite=Lax';
  } catch {}
}

export const setToken = (t) => {
  markStaffCookie(!!t);
  return t ? localStorage.setItem(STORAGE_KEY, t) : localStorage.removeItem(STORAGE_KEY);
};

// staršie prihlásenie cookie ešte nemá – doplníme ju pri načítaní aplikácie
if (typeof document !== 'undefined' && localStorage.getItem(STORAGE_KEY)) markStaffCookie(true);

export function apiUrl(path) {
  return `${getServerUrl()}${path}`;
}

let nahladPartnera = null;
export const nastavNahladPartnera = (id) => { nahladPartnera = id ? Number(id) : null; };

async function request(method, path, body, opts = {}) {
  const headers = { ...(opts.headers || {}) };
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  /* .816 Náhľad partnerského portálu – admin vidí portál očami partnera. */
  if (nahladPartnera && String(path).startsWith('/api/partner/')) headers['X-Nahlad-Partner'] = String(nahladPartnera);
  let payload = body;
  if (body && !(body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
    payload = JSON.stringify(body);
  }
  const res = await fetch(apiUrl(path), { method, headers, body: payload });
  if (res.status === 401) {
    setToken(null);
    if (!location.pathname.startsWith('/prihlasenie')) location.href = '/prihlasenie';
    throw new Error('Neprihlásený');
  }
  const ct = res.headers.get('content-type') || '';
  if (!res.ok) {
    let msg = `Chyba ${res.status}`;
    let telo = null;
    if (ct.includes('json')) {
      try { telo = await res.json(); msg = telo.error || msg; } catch {}
    }
    const e = new Error(msg);
    /* Podrobnosti z odpovede idú so sebou. Niektoré obrazovky ich vedia
     * ukázať (napr. čo odpovedal Chameleoon na jednotlivé tvary prihlásenia)
     * a bez nich by ostala len holá veta „odmietol kľúč". */
    if (telo) e.data = telo;
    throw e;
  }
  if (opts.blob) return res.blob();
  if (ct.includes('json')) return res.json();
  return res.text();
}

export const api = {
  get: (p, o) => request('GET', p, null, o),
  post: (p, b, o) => request('POST', p, b, o),
  put: (p, b, o) => request('PUT', p, b, o),
  patch: (p, b, o) => request('PATCH', p, b, o),
  /* Aj DELETE smie nosiť telo – niektoré odobranie sa robí zoznamom kódov. */
  del: (p, b) => request('DELETE', p, b ?? null),

  login: (email, password) => request('POST', '/api/auth/login', { email, password }),
  me: () => request('GET', '/api/auth/me'),

  /** Otvorí PDF v novom okne (s tokenom v query). */
  openPdf(path) {
    const sep = path.includes('?') ? '&' : '?';
    window.open(apiUrl(`${path}${sep}token=${encodeURIComponent(getToken() || '')}`), '_blank');
  },
};

/* ---------- pomocné formátovače ---------- */
export const money = (n, cur = 'EUR') =>
  new Intl.NumberFormat('sk-SK', { style: 'currency', currency: cur }).format(Number(n || 0));

/* .974 cena za kus: aspoň 2, najviac 4 desatinné miesta (0,7848 €) */
export const cenaKus = (n, cur = 'EUR') =>
  new Intl.NumberFormat('sk-SK', { style: 'currency', currency: cur, minimumFractionDigits: 2, maximumFractionDigits: 4 }).format(Number(n || 0));

export const num = (n, max = 2) =>
  new Intl.NumberFormat('sk-SK', { maximumFractionDigits: max }).format(Number(n || 0));

export const dateSk = (d) => {
  if (!d) return '';
  const s = String(d).slice(0, 10).split('-');
  return s.length === 3 ? `${Number(s[2])}. ${Number(s[1])}. ${s[0]}` : d;
};

/* ---------- čas zo servera ----------
 *
 * V databáze sú časy uložené v **UTC** a bez značky pásma („2026-08-26
 * 06:13:00"). Prehliadač takýto zápis chápe ako miestny čas, a preto by
 * všetko ukazoval o dve hodiny (v zime o hodinu) skôr, než sa to naozaj
 * stalo. Preto sa taký čas číta ako UTC a vypisuje sa v čase predajne.
 *
 * Hodnota, ktorá si pásmo nesie sama („…Z" alebo „+02:00"), sa nechá tak.
 * Samotný dátum bez času nie je okamih, ale deň v kalendári – ten sa
 * neprepočítava, inak by sa okolo polnoci posúval.
 */
export const CAS_PASMO = 'Europe/Bratislava';

export function casZoServera(v) {
  if (!v) return null;
  const s = String(v).trim();
  if (s.length <= 10) {
    const d = new Date(`${s}T12:00:00`);       // deň, nie okamih
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const maPasmo = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(s);
  const d = new Date(maPasmo ? s.replace(' ', 'T') : `${s.replace(' ', 'T')}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Dátum a čas v čase predajne, napr. „26. 8. 2026, 8:25". */
export function dtSk(v, { rok = 'numeric', sekundy = false, prazdne = '' } = {}) {
  const d = casZoServera(v);
  if (!d) return prazdne;
  return d.toLocaleString('sk-SK', {
    timeZone: CAS_PASMO,
    day: 'numeric', month: 'numeric', year: rok,
    hour: '2-digit', minute: '2-digit', ...(sekundy ? { second: '2-digit' } : {}),
  });
}

/** Len čas, napr. „8:25". */
export function casSk(v, prazdne = '') {
  const d = casZoServera(v);
  return d ? d.toLocaleTimeString('sk-SK', { timeZone: CAS_PASMO, hour: '2-digit', minute: '2-digit' }) : prazdne;
}

/** Deň v kalendári podľa času predajne – nie podľa UTC. */
const denVPasme = (d, pasmo = CAS_PASMO) =>
  new Intl.DateTimeFormat('en-CA', { timeZone: pasmo, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);

export const today = () => denVPasme(new Date());
export const addDays = (days) => denVPasme(new Date(Date.now() + days * 86400000));

/**
 * „dnes", „včera", „pred 3 dňami".
 *
 * Ráta sa rozdiel **kalendárnych dní v čase predajne**, nie uplynulých hodín.
 * Odčítať dva okamihy je tu chyba, ktorá sa prejaví až popoludní: príjem
 * z dnešného rána je „pred 0,9 dňa", zaokrúhli sa na jeden – a značka na
 * čerstvo naskladnenom tovare hlási „včera". Časy z databázy sú navyše v UTC,
 * takže deň sa musí brať v pásme predajne, inak tovar prijatý po polnoci
 * pripadne na predchádzajúci deň.
 */
export function predDnami(v, prazdne = '') {
  const d = casZoServera(v);
  if (!d) return prazdne;
  const dni = Math.round(
    (Date.parse(`${today()}T00:00:00Z`) - Date.parse(`${denVPasme(d)}T00:00:00Z`)) / 86400000,
  );
  if (dni <= 0) return 'dnes';
  if (dni === 1) return 'včera';
  if (dni < 31) return `pred ${dni} dňami`;
  if (dni < 365) return `pred ${Math.round(dni / 30)} mes.`;
  return `pred ${Math.round(dni / 365)} r.`;
}

export const r2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
